import classNames from "classnames";
import { useEffect, useState } from "react";
import { BaseButton } from "../../../../../../components/Button/BaseButton.tsx";
import { BaseClickableIcon } from "../../../../../../components/Icon/BaseClickableIcon.tsx";
import { ClickableIcon } from "../../../../../../components/Icon/ClickableIcon.tsx";
import { Query } from "../../../../../../components/Query/Query.tsx";
import { Spinner } from "../../../../../../components/Spinner/Spinner.tsx";
import { useAppMutation } from "../../../../../../http/useAppMutation.ts";
import { notifier } from "../../../../../../utils/notifier.tsx";
import { ThumbnailGeneratedContent } from "../../../../components/GeneratedContent/ThumbnailGeneratedContent.tsx";
import { PanelSection } from "../../../../components/Panel/PanelSection.tsx";
import { TOOL_DISPLAYABLE_NAMES } from "../../../../display.ts";
import { useBoardUuid } from "../../../../hooks/useBoardUuid.ts";
import { useSelectedAsset } from "../../../../hooks/useSelectedAsset.ts";
import { useUpdateAsset } from "../../../../hooks/useUpdateAsset.ts";
import type {
  AssetEdition,
  AssetHistory,
  Content,
  DetailedAsset,
} from "../../../../types.ts";
import { overlayDisplayedContentUuidStore } from "../../stores/overlayDisplayedContentUuidStore.ts";

export const EditionHistorySection = () => {
  const { selectedAsset } = useSelectedAsset();

  return (
    <PanelSection title="History">
      {selectedAsset ? (
        <Query
          queryKey={`assets/${selectedAsset.uuid}/history`}
          enabled={!!selectedAsset}
        >
          {(editionHistory) => (
            <EditionHistorySectionContent
              selectedAsset={selectedAsset}
              editionHistory={editionHistory}
            />
          )}
        </Query>
      ) : (
        <Spinner />
      )}
    </PanelSection>
  );
};

const EditionHistorySectionContent = ({
  selectedAsset,
  editionHistory,
}: {
  selectedAsset: DetailedAsset;
  editionHistory: AssetHistory;
}) => {
  useEffect(() => {
    overlayDisplayedContentUuidStore.clearOverlayDisplayedContentUuid();
    return () =>
      overlayDisplayedContentUuidStore.clearOverlayDisplayedContentUuid();
  }, []);

  return editionHistory.editions
    .sortDesc((it) => it.created_at)
    .map((edition, index) => (
      <Edition
        key={edition.uuid}
        edition={edition}
        selectedContent={selectedAsset.content}
        isOriginal={index === editionHistory.editions.length - 1}
      />
    ));
};

const Edition = ({
  edition,
  selectedContent,
  isOriginal,
}: {
  edition: AssetEdition;
  selectedContent: Content;
  isOriginal: boolean;
}) => {
  const editionSelectedContent = edition.contents.find(
    (content) => content.uuid === selectedContent.uuid,
  );
  const isSelected = !!editionSelectedContent;
  const [isUnfolded, setIsUnfolded] = useState(isSelected);
  return (
    <div className="flex-col">
      <div className="pr-150 flex-row items-center gap-200">
        <BaseClickableIcon
          name="ChevronRight"
          size="sm"
          rotate={isUnfolded ? 90 : 0}
          onClick={() => setIsUnfolded((unfolded) => !unfolded)}
          iconClassName={classNames(
            "stroke-lg",
            isSelected ? "stroke-button-primary-rest" : "",
          )}
          className={classNames(
            "rounded-full p-100",
            isSelected
              ? "bg-surface-inverse-rest hover:bg-surface-inverse-hover"
              : "bg-surface-secondary-rest hover:bg-surface-secondary-hover",
          )}
        />
        <div className="flex-fill body-md-semibold">
          {isOriginal
            ? "Original"
            : edition.generation_type
            ? TOOL_DISPLAYABLE_NAMES[edition.generation_type]
            : "Other"}
        </div>
        {editionSelectedContent && (
          <EditionThumbnail content={editionSelectedContent} />
        )}
      </div>
      <div className="flex-row gap-200">
        <div className={classNames("mx-300", isOriginal ? "" : "border-l")} />
        <div className="flex-fill pb-300">
          {isUnfolded && (
            <div
              className="grid grid-cols-2 gap-050 py-100"
              onMouseLeave={() => {
                overlayDisplayedContentUuidStore.clearOverlayDisplayedContentUuid();
              }}
            >
              {edition.contents.map((content) => (
                <EditionContentTile
                  key={content.uuid}
                  content={content}
                  isSelected={selectedContent.uuid === content.uuid}
                  isDeleteButtonDisabled={
                    // XXX: We don't allow to delete the original content or the last content of an edition
                    isOriginal || edition.contents.length < 2
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EditionThumbnail = ({ content }: { content: Content }) => (
  <div className="h-600 w-600 rounded-150 overflow-hidden">
    <ThumbnailGeneratedContent content={content} cover spinner />
  </div>
);

const EditionContentTile = ({
  content,
  isSelected,
  isDeleteButtonDisabled,
}: {
  content: Content;
  isSelected: boolean;
  isDeleteButtonDisabled: boolean;
}) => {
  const { selectedAsset } = useSelectedAsset();
  const { updateAsset, isUpdateAssetLoading } = useUpdateAsset();

  return (
    <div
      className={classNames(
        "relative group aspect-square border-050 rounded-200 flex-col-center",
        isSelected ? "border-input-border-active" : "border-[--grey-1]",
      )}
      onMouseEnter={() => {
        overlayDisplayedContentUuidStore.setOverlayDisplayedContentUuid(
          content.uuid,
        );
      }}
    >
      <BaseButton
        key={content.uuid}
        className={classNames(
          "p-050 max-w-full max-h-full rounded-200 overflow-hidden bg-surface-secondary-rest flex-fill",
          isSelected
            ? ""
            : isUpdateAssetLoading
            ? "opacity-60"
            : "opacity-70 hover:opacity-100 hover:bg-surface-secondary-hover",
        )}
        onClick={() => {
          if (!isSelected) updateAsset(content.uuid);
        }}
      >
        <ThumbnailGeneratedContent content={content} cover={false} />
      </BaseButton>
      <div className="absolute top-100 right-100 invisible group-hover:visible">
        <CopyContentAsAssetButton contentUuid={content.uuid} />
      </div>
      {selectedAsset && (
        <div className="absolute bottom-100 right-100 invisible group-hover:visible">
          <DeleteContentButton
            contentUuid={content.uuid}
            selectedAssetUuid={selectedAsset.uuid}
            disabled={isDeleteButtonDisabled}
          />
        </div>
      )}
      {isUpdateAssetLoading && (
        <div className="absolute">
          <Spinner size="sm" />
        </div>
      )}
    </div>
  );
};

const CopyContentAsAssetButton = ({ contentUuid }: { contentUuid: string }) => {
  const boardUuid = useBoardUuid();

  // FIXME: When creating asset from existing content, we duplicate also the whole history of the asset. This could be
  //  fixed by saving in the asset table an original content from which computing the history.
  const { mutate: createAsset } = useAppMutation({
    path: "assets/create",
    invalidate: [`boards/${boardUuid}/assets`],
    onSuccess: () => {
      notifier.success("New asset created successfully!");
    },
    onError: () => {
      notifier.error("Failed to create new asset");
    },
  }).mutation;

  return (
    <ClickableIcon
      name="CopyPlus"
      variant="inverse-primary"
      size="sm"
      tooltip={{
        side: "bottom",
        content: "Save copy as new asset",
      }}
      onClick={() =>
        createAsset({
          board_uuid: boardUuid,
          content_uuid: contentUuid,
        })
      }
    />
  );
};

const DeleteContentButton = ({
  contentUuid,
  selectedAssetUuid,
  disabled,
}: {
  contentUuid: string;
  selectedAssetUuid: string;
  disabled?: boolean;
}) => {
  const deleteContents = useAppMutation({
    path: "contents/delete",
    invalidate: [
      `assets/${selectedAssetUuid}`,
      `assets/${selectedAssetUuid}/history`,
    ],
  }).mutation;

  return (
    <ClickableIcon
      name="Trash"
      variant="inverse-primary"
      size="sm"
      disabled={disabled}
      tooltip={{
        side: "bottom",
        content: disabled
          ? "You cannot delete the last image of an edition"
          : "Delete image",
      }}
      onClick={() =>
        deleteContents.mutate({
          uuids: [contentUuid],
        })
      }
    />
  );
};
